import React from 'react';

type Props = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
  error?: Error;
};

export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep logs for debugging (can be wired to monitoring later)
    console.error('Unhandled UI error:', error, info);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-neutral flex items-center justify-center py-16">
          <div className="container">
            <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-soft p-10 text-center">
              <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-error text-3xl font-bold">!</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-display font-bold text-secondary mb-3">
                Something went wrong
              </h1>
              <p className="text-accent mb-8">
                Please refresh the page. If the problem continues, let us know.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button className="btn btn-primary" onClick={this.handleReload}>
                  Reload Page
                </button>
                <button className="btn btn-outline" onClick={this.handleGoHome}>
                  Go to Home
                </button>
              </div>
              {process.env.NODE_ENV !== 'production' && this.state.error && (
                <pre className="mt-8 text-left text-xs bg-neutral rounded-xl p-4 overflow-auto text-secondary">
                  {this.state.error.message}
                </pre>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

